"use strict";
/*jshint esversion: 11 */

import React, {useEffect, useRef, useState, useCallback} from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from "prop-types";
import CsfMembrePageHeading
  from "../csf-membre-page-heading/csf-membre-page-heading";
import CsfMembreSideMenu from "../csf-membre-side-menu/csf-membre-side-menu";
import CsfMembreReportUfcModal from "./CsfMembreReportUfcModal";
import {
  startLoadingSpinner,
  stopLoadingSpinner
} from "../../csf-ufc-pivotal/includes/utils";
import CsfMemberDataLayer from "../utils/CsfMembreDataLayer";
import CsfMembreUtils from "../utils/CsfMembreUtils";
import PivotalFormApi from "../../../js/csf/pivotal/PivotalFormApi";
import PivotalApiEnvironments
  from "../../../js/csf/pivotal/PivotalApiEnvironments";

export function CsfMembreReportUfc({apiUrl, apiEnv, urlDossierUfc, urlNotifications}) {
  const [isError, setIsError] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [member, setMember] = useState({});
  const [ufcCase, setUfcCase] = useState({});
  const [ufcHistory, setUfcHistory] = useState({});
  const [checkedPrintOption, setCheckedPrintOption] = useState('');
  const [dataLayer, setDataLayer] = useState({});
  const [reports, setReports] = useState([]);
  const [previousPeriodId, setPreviousPeriodId] = useState('');
  const [prioritySelections, setPrioritySelections] = useState({});
  const [canModify, setCanModify] = useState(false);
  const [redirectionUrl, setRedirectionUrl] = useState(null);
  const [modalInstance, setModalInstance] = useState(null);

  // changements à sauvegarder vers l'API:
  // Structure : { [subjectId]: { priority: <string/number>, reportedUfc: <number> } }
  const [reportState, setReportState] = useState({});
  const totalReportedUfc = Object.values(reportState).reduce((sum, entry) => sum + (entry.reportedUfc || 0), 0);
  const langcode = document.documentElement.lang;
  const ufcTableRef = useRef();

  useEffect(() => {
    startLoadingSpinner();
    CsfMemberDataLayer.fetchAccessToken(setAccessToken);
    document.addEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (accessToken) {
      setDataLayer(new CsfMemberDataLayer(apiUrl, apiEnv, accessToken));
    }
  }, [accessToken]);

  useEffect(() => {
    if (CsfMembreUtils.objectIsEmpty(member) && !CsfMembreUtils.objectIsEmpty(dataLayer)) {
      dataLayer.getMember(setMember);
    }
  }, [dataLayer]);

  useEffect(() => {
    setUfcCaseFromAPI();
  }, [member]);

  useEffect(() => {
    if (!canModify) {
      setUfcCaseFromAPI();
    }
  }, [canModify]);

  useEffect(() => {
    if (!CsfMembreUtils.objectIsEmpty(member) && !CsfMembreUtils.objectIsEmpty(dataLayer)) {
      if(!CsfMembreUtils.objectIsEmpty(ufcCase?.Education_File__Secondary[0])) {
        dataLayer.getUfcHistoryInfo(ufcCase?.Education_File__Secondary[0]?.Education_File_Id, setUfcHistory);
      }
    }
  }, [ufcCase]);

  useEffect(() => {
    if (!CsfMembreUtils.objectIsEmpty(ufcHistory)) {

      const prevPeriodObj = dataLayer.getPreviousPeriodHistoryInfo(ufcHistory);
      if (prevPeriodObj?.REDU_Edu_File__Period_Id) {
        setPreviousPeriodId(prevPeriodObj.REDU_Edu_File__Period_Id);
        getMemberUFCReportList(prevPeriodObj.REDU_Edu_File__Period_Id, (data) => {
          setReports(data);
          const mapped = data.reduce((acc, item) => {
            acc[item.REDU_Edu_File__Subject_Id] = String(item.Priorisation ?? "");
            return acc;
          }, {});
          setPrioritySelections(mapped);
        });
      }

    }
  }, [ufcHistory]);

  useEffect(() => {
    if (reports.length > 0) {
      const calculated = calculateReportState(prioritySelections, reports);
      setReportState(calculated);
    }
  }, [prioritySelections, reports]);

  function  setUfcCaseFromAPI(){
    if (!CsfMembreUtils.objectIsEmpty(member) && !CsfMembreUtils.objectIsEmpty(dataLayer)) {
      dataLayer.getMemberUFCCase(member?.Contact_Id, setUfcCase);
    }
  }

  // effectuer le calcul des UFC reportés, selon les priorités sélectionnées
  function calculateReportState(prioritySelections, reports) {
    const maxReportable = 5;

    // Récupère tableau des [subjectId, priorité]
    const selectedPriorities = Object.entries(prioritySelections)
      .filter(([_, v]) => v) // ignore sans sélection
      .map(([subjectId, priority]) => ({
        subjectId,
        priority: parseInt(priority),
        ufcExcedent: Number(reports.find(rep => rep.REDU_Edu_File__Subject_Id === subjectId)?.UFC_Excedent_Web ?? 0)
      }))
      .sort((a, b) => a.priority - b.priority);

    let remaining = maxReportable;
    const newReportState = {};

    selectedPriorities.forEach(({subjectId, priority, ufcExcedent}) => {
      const reportable = Math.min(ufcExcedent, remaining);
      newReportState[subjectId] = {
        priority,
        reportedUfc: reportable
      };
      remaining -= reportable;
    });

    // Pour les autres catégories, conserver la priorité et 0 UFC reporté
    reports.forEach(item => {
      if (!newReportState[item.REDU_Edu_File__Subject_Id]) {
        newReportState[item.REDU_Edu_File__Subject_Id] = {
          priority: prioritySelections[item.REDU_Edu_File__Subject_Id] || "",
          reportedUfc: 0
        };
      }
    });

    return newReportState;
  }


  function getMemberUFCReportList(previousPeriodId, setUfcCaseCallback) {
    const requestData = {
      form: "CSF_EM_Report_UFC_Info",
      recordId: previousPeriodId
    };
    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
    pivotalFormApi.callFormRecuperationEndpoint(requestData, PivotalApiEnvironments.getEspaceMembreSuffix())
      .then(response => {
        if (response?.success && response?.payload?.data?.primary?.NewSegment__Secondary) {

          // TODO retirer ce data de test
          // pour tests: mettre une valeur forcee pour UFC_Excedent_Web
          // let testData = response.payload.data.primary.NewSegment__Secondary;
          // testData.forEach(item => {
          //   item.UFC_Excedent_Web = 3;
          // });
          // setUfcCaseCallback(testData);
          // fin tests

          setUfcCaseCallback(response.payload.data.primary.NewSegment__Secondary);
        }
      })
      .catch(error => {
        console.error(error);
      });
  }


  // lors de la sélection d'une valeur de priorité:
  // assurer que la priorité sélectionnée dans la liste des reports est unique
  function handleUfcPrioritySelectChange(event, subjectId) {
    const selectedValue = event.target.value;

    setPrioritySelections(prevSelections => {
      // Cherche s'il y a une autre entrée avec la même valeur (autre que celle en cours)
      const duplicateKey = Object.keys(prevSelections).find(
        key => prevSelections[key] === selectedValue && key !== subjectId
      );

      // Crée la nouvelle sélection: enlève la valeur de l'ancien "propriétaire" le cas échéant
      let newSelections = { ...prevSelections, [subjectId]: selectedValue };
      if (duplicateKey) {
        newSelections[duplicateKey] = ""; // on "vide" l'ancien
      }

      return newSelections;
    });
  }

  function savePriorisation() {
    startLoadingSpinner();
    // Les données à sauvegarder
    const dataToSave = reports.map(item => ({
      REDU_Edu_File__Subject_Id: item.REDU_Edu_File__Subject_Id,
      REDU_Subject_Id: item.REDU_Subject_Id,
      // must we pass UFC_Excedent_Web ?
      //UFC_Excedent_Web: item.UFC_Excedent_Web,
      Priorisation: reportState[item.REDU_Edu_File__Subject_Id]?.priority  ?? "",
      // must we pass UFC_Postponed_Previous ?
      //UFC_Postponed_Previous: reportState[item.REDU_Edu_File__Subject_Id]?.reportedUfc || 0
    }));

    // Vérifie que toutes les priorités sont sélectionnées avant d'activer la sauvegarde
    const allFilled = dataToSave.every(e => e.Priorisation !== "" && e.Priorisation !== undefined && e.Priorisation !== null);

    if (allFilled) {
      // appel à API pour sauvegarde
      const requestFormData = {
        form: "CSF_EM_Report_UFC_REST",
        recordId: previousPeriodId,
        deltaData: {
          NewSegment__Secondary:dataToSave
        }
      };
      const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
      pivotalFormApi.callFormPutEndpoint(requestFormData, PivotalApiEnvironments.getEspaceMembreSuffix())
        .then(response => {
          stopLoadingSpinner();
          // response to the save
          console.log(response);
          if (response?.success) {
            window.location.reload();
          } else {
            setIsError(true);
          }
        })
        .catch(error => {
          stopLoadingSpinner();
          console.error(error);
          setIsError(true);
        });
      setCanModify(false);
    } else {
      console.log("Toutes les priorités doivent être sélectionnées avant de sauvegarder!");
    }
  }

  function getUfcReportPrioritiesLabel(priorityValue) {
    const priorityLabels = {
      0 : window.Drupal.t('member.reportufc.priority.label.0'),
      1 : window.Drupal.t('member.reportufc.priority.label.1'),
      2 : window.Drupal.t('member.reportufc.priority.label.2'),
      3 : window.Drupal.t('member.reportufc.priority.label.3'),
      4 : window.Drupal.t('member.reportufc.priority.label.4'),
      5 : window.Drupal.t('member.reportufc.priority.label.5'),
    };
    return priorityLabels[priorityValue] || [];
  }


    /*
    * Init the bootstrap tooltips on list change
    * */
  useEffect(() => {
    $('[data-toggle="tooltip"]').tooltip({
      customClass: 'wide-tooltip',
      html: true
    });
  }, []);

  // Show a warning modal if user tries to leave the page without saving
  const handleClick = useCallback((e) => {
    if (canModify) {
      const target = e.target.closest('a, button');
      if (!target) return;

      e.preventDefault();
      e.stopPropagation();

      if (target.tagName === 'A' && target.href && target.href !== "#") {
        setRedirectionUrl(target.href);
        modalInstance.show();
      }

      // make sure a button from side-menu is clicked and that it's not opening a modal nor from a modal
      if (target.tagName === 'BUTTON' && e.target.closest('.membre__side-menu') && !target.dataset.target && !e.target.closest('.modal')) {
        modalInstance.show();
      }
    }
  }, [canModify]);

  // This below MUST be placed after the handleClick const
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  function handleContinue() {
    if (redirectionUrl !== '') {
      window.location.href = redirectionUrl;
    }
  }

  return (
    <main className="membre report-ufc">
      <div className="content">
        <div className="primary non-print-wrapper">
          <section className="report__top-section primary-bg mb-5 py-5">
            <p className="text-bold mb-2 text-primary">{window.Drupal.t('member.reportufc.box.title')}</p>
            <p className="text-primary">{ window.Drupal.t('member.reportufc.box.text')}</p>
            <div className="d-flex align-items-center mt-3">
              <span className="flex-shrink-0 mr-1">
                <i className="icon icon-member-exclamation bg-primary"><span className="d-none">&nbsp;</span></i>
              </span>
              <p className="mb-0" dangerouslySetInnerHTML={{ __html: window.Drupal.t('member.reportufc.box.warning')}}></p>
            </div>
          </section>

          {/* main section */}
          <section className="primary-bg">
            {/* sub title */}

            <CsfMembrePageHeading
              title={window.Drupal.t('member.reportufc.title')}
              memberName={member["Prénom"] || "" + ' ' + member["Nom"] || ""}
              certificatNumber={member["No_Dossier"] || ""}
            />

            {isError && (
              <div className="alert alert-danger mb-4" role="alert"
                aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
            )}
            <div className="inner-wrapper">
              <div className="table-wrapper">
                <table className="report__table" ref={ufcTableRef}>
                  <thead>
                      <tr>
                        <th>
                          {window.Drupal.t('Category')}
                        </th>
                        <th>
                          <div className="d-flex align-items-center justify-content-center">
                            <p className="m-0" dangerouslySetInnerHTML={{ __html: window.Drupal.t('member.reportufc.exceedingUfc')}}></p>
                            <span className="visually-hidden">{window.Drupal.t('member.reportufc.tooltip.exceedingUfc')}</span>
                            <img className="ml-1" src="/themes/custom/tink/dist/assets/icon-membre-tooltip.svg" data-toggle="tooltip"
                              title={window.Drupal.t('member.reportufc.tooltip.exceedingUfc')} alt="" />
                          </div>
                        </th>
                        <th width="206">
                          <div className="d-flex align-items-center justify-content-center">
                            <p className="m-0" dangerouslySetInnerHTML={{ __html: window.Drupal.t('member.reportufc.priority')}}></p>
                            <span className="visually-hidden" dangerouslySetInnerHTML={{ __html: window.Drupal.t('member.reportufc.tooltip.priority')}}></span>
                            <img className="ml-1" src="/themes/custom/tink/dist/assets/icon-membre-tooltip.svg" data-toggle="tooltip"
                              title={window.Drupal.t('member.reportufc.tooltip.priority')} alt="" />
                          </div>
                        </th>
                        <th>
                          <p className="m-0" dangerouslySetInnerHTML={{ __html: window.Drupal.t('member.reportufc.postponedUfc')}}></p>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                    {reports?.length === 0 && (
                      <tr>
                        <td colSpan="6">
                          <p>{window.Drupal.t('fournisseur.empty.training.list')}</p>
                        </td>
                      </tr>
                    )}
                    {reports.map(item => (
                      <tr key={item.REDU_Edu_File__Subject_Id}>
                        <td>
                          {langcode === 'fr' ?  item.Subject_Name_French : item.Subject_Name_English}
                        </td>
                        <td>
                          {item.UFC_Excedent_Web}
                        </td>
                        <td>
                          <div className="form-group text-left m-0">
                            <label htmlFor={item.REDU_Edu_File__Subject_Id}
                                  className={!prioritySelections[item.REDU_Edu_File__Subject_Id] ? '--label-only no-style-label' : ''}
                              >{window.Drupal.t('member.reportufc.select.label')}
                            </label>
                            <select id={item.REDU_Edu_File__Subject_Id}
                                    name={item.REDU_Edu_File__Subject_Id}
                                    onChange={e => handleUfcPrioritySelectChange(e, item.REDU_Edu_File__Subject_Id)}
                                    value={prioritySelections[item.REDU_Edu_File__Subject_Id] || ""}
                                    disabled={!canModify}
                            >
                              <option value="" disabled></option>
                              {reports.map((_, index) => (
                                <option key={'opt_priority_'+index} value={index}>
                                  {getUfcReportPrioritiesLabel(index)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className={canModify ? '--canModify' : ''}>
                          {/*UFC reportés, populé automatiquement selon value excédentaire et range choice */}
                          {reportState[item.REDU_Edu_File__Subject_Id]?.reportedUfc || 0}
                        </td>
                      </tr>
                    ))}
                    <tr key="range-total" className="reports__row-total">
                      <td colSpan="3" dangerouslySetInnerHTML={{ __html: window.Drupal.t('member.reportufc.total')}}>
                      </td>
                      <td className={`text-primary${canModify ? ' --canModify' : ''}`}>
                        {/* TOTAL des ufc reportés, populé automatiquement */}
                        {totalReportedUfc}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              { !canModify && (
                <div className="space-between-wrapper">
                  <button
                    className="button btn-pale btn-larger mt-3"
                    onClick={() => setCanModify(true)}
                  >
                    {window.Drupal.t('edit.btn')}
                  </button>
                </div>
              )}
              {canModify && (
                <div className="space-between-wrapper">
                  <button
                    className="button btn-outlined btn-larger mt-3"
                    onClick={() => setCanModify(false)}
                  >
                    {window.Drupal.t('fournisseur.back.btn')}
                  </button>
                  <button
                    className="button btn-pale btn-larger mt-3"
                    onClick={savePriorisation}
                    disabled={reports.length > 0 && Object.values(reportState).filter(r => r.priority !== "" && r.priority !== undefined && r.priority !== null).length !== reports.length}
                  >
                    {window.Drupal.t('fournisseur.delegation.modal.edit.submit')}
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* aside section */}
        <CsfMembreSideMenu
          apiUrl={apiUrl}
          apiEnv={apiEnv}
          urlDossierUfc={urlDossierUfc}
          urlNotifications={urlNotifications}
          checkedPrintOption={checkedPrintOption}
          setCheckedPrintOption={setCheckedPrintOption}
          preventRedirect={canModify}
          setRedirectionUrl={setRedirectionUrl}
        />
        <CsfMembreReportUfcModal
          handleContinue={handleContinue}
          setModalInstance={setModalInstance}
        />
      </div>
    </main>
  );
}

window.addEventListener('DOMContentLoaded', () => {
  const domNode = document.getElementById('CsfMembreReportUfc');

  if (domNode !== null) {
    const root = createRoot(domNode);

    const apiUrl = domNode.getAttribute("data-api-url");
    const apiEnv = domNode.getAttribute("data-api-env");
    const urlDossierUfc = domNode.getAttribute("data-urlSectionMembreDossierUfc");
    const urlNotifications = domNode.getAttribute("data-urlSectionMembreNotifications");

    root.render(<CsfMembreReportUfc
      apiUrl={apiUrl}
      apiEnv={apiEnv}
      urlDossierUfc={urlDossierUfc}
      urlNotifications={urlNotifications}
    />);
  }
});

CsfMembreReportUfc.propTypes = {
  apiUrl: PropTypes.string,
  apiEnv: PropTypes.string,
  urlDossierUfc: PropTypes.string,
  urlNotifications: PropTypes.string,
};
